import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { VatCountryCode } from '~/api/common/types';

import { CountryFlag } from '~/components';
import { Combobox, Field } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';

import { useVatCountryCodeGroups, type VatCountryCodeGroup } from '../../../components/useVatCountryCodeGroups';
import { mapVatCountryCodeToCountryCode } from '../../../utils';
import { useCreateClientContext } from '../../context';
import { useCreateOrganisationContext } from '../context';
import { countryNameStyle, countryStyle } from '../organisationStep.css';

const CountryRow = ({ countryCode, label }: { countryCode: VatCountryCode; label: string }) => (
  <span className={countryStyle}>
    <CountryFlag countryCode={mapVatCountryCodeToCountryCode(countryCode)} />
    <span className={countryNameStyle}>{label}</span>
  </span>
);

export const VatCountryCodeSelect = ({ disabled = false }: { disabled?: boolean }) => {
  const { resetClientContext } = useCreateClientContext();
  const { setVatCountryCode, vatCountryCode } = useCreateOrganisationContext();

  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['clients']);

  const getCountryName = useCallback(
    (countryCode: VatCountryCode) => displayNameOfCountry(mapVatCountryCodeToCountryCode(countryCode)),
    [displayNameOfCountry],
  );

  const countryCodeGroups = useVatCountryCodeGroups();

  return (
    <Field.Root name="vatCountryCode">
      <Field.Label>{t('clients:fields.countryCode.label')}</Field.Label>

      <Combobox.Root
        disabled={disabled}
        items={countryCodeGroups}
        itemToStringLabel={getCountryName}
        onValueChange={(countryCode) => {
          if (countryCode === null) return;

          setVatCountryCode(countryCode);

          // Reset contexts to default state on country code change
          resetClientContext();
        }}
        value={vatCountryCode}
      >
        <Combobox.Button adornment={<CountryFlag countryCode={mapVatCountryCodeToCountryCode(vatCountryCode)} />} clearable={false} />

        <Combobox.List>
          {(group: VatCountryCodeGroup) => (
            <Combobox.Group items={group.items} key={group.label}>
              <Combobox.GroupLabel>{group.label}</Combobox.GroupLabel>

              <Combobox.Collection>
                {(countryCode: VatCountryCode) => (
                  <Combobox.Item key={countryCode} value={countryCode}>
                    <CountryRow countryCode={countryCode} label={getCountryName(countryCode)} />
                  </Combobox.Item>
                )}
              </Combobox.Collection>
            </Combobox.Group>
          )}
        </Combobox.List>
      </Combobox.Root>
    </Field.Root>
  );
};
